// ./src/hooks/usePdfDownload.ts
import { useState, useCallback } from "react";
import { notifications } from "@mantine/notifications";

import { Submission, Form, FormField } from "@/utils/api";
import { generateSubmissionPdf } from "@/utils/generateSubmissionPdf";

export interface UsePdfDownloadOptions {
  /** Submission data */
  submission: Submission | null;
  /** Form configuration */
  form: Form | null;
  /** Form field definitions */
  formFields?: FormField[];
  /** Whether to show logo (based on plan) */
  showLogo?: boolean;
}

export interface UsePdfDownloadReturn {
  /** Whether PDF is currently being generated */
  isGenerating: boolean;

  /** Download PDF with form data only */
  downloadPdf: () => void;

  /** Download PDF with metadata included */
  downloadPdfWithMetadata: () => void;
}

export const usePdfDownload = (
  options: UsePdfDownloadOptions
): UsePdfDownloadReturn => {
  const { submission, form, formFields = [], showLogo = true } = options;

  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Generate and download PDF
   */
  const generatePdf = useCallback(
    async (includeMetadata: boolean) => {
      if (!submission || !form) {
        notifications.show({
          color: "red",
          title: "Error",
          message: "Submission or form data is not available",
        });
        return;
      }

      setIsGenerating(true);

      // Show loading notification
      notifications.show({
        id: "pdf-generation",
        loading: true,
        title: "Generating PDF",
        message: "Please wait while we generate your PDF...",
        autoClose: false,
        withCloseButton: false,
      });

      try {
        // Small delay to allow UI to update
        await new Promise((resolve) => setTimeout(resolve, 100));

        await generateSubmissionPdf({
          submission,
          form,
          formFields:
            formFields.length > 0 ? formFields : form.formFields || [],
          includeMetadata,
          showFullCreditCard: false, // Always mask in PDF for security
          showLogo,
        });

        notifications.update({
          id: "pdf-generation",
          color: "green",
          title: "PDF Generated",
          message: "Your PDF has been downloaded successfully",
          loading: false,
          autoClose: 3000,
        });
      } catch (error) {
        console.error("Error generating PDF:", error);
        notifications.update({
          id: "pdf-generation",
          color: "red",
          title: "Error",
          message: "Failed to generate PDF. Please try again.",
          loading: false,
          autoClose: 5000,
        });
      } finally {
        setIsGenerating(false);
      }
    },
    [submission, form, formFields, showLogo]
  );

  /**
   * Download PDF with form data only
   */
  const downloadPdf = useCallback(() => {
    generatePdf(false);
  }, [generatePdf]);

  /**
   * Download PDF with metadata included
   */
  const downloadPdfWithMetadata = useCallback(() => {
    generatePdf(true);
  }, [generatePdf]);

  return {
    isGenerating,
    downloadPdf,
    downloadPdfWithMetadata,
  };
};
